import {
  useAtom,
  useAtomValue,
  useSetAtom,
} from 'infrastructure/state/jotai.wrapper'
import { partnerIdAtom } from '../../components/ingestion/atoms'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
} from '@mui/material'
import React, { useEffect } from 'react'
import { useParams } from 'infrastructure/routing'
import {
  configFileAtom,
  configurationFromSourceAtom,
  editedConfigAtom,
  userMessageAtom,
} from './atoms'
import { ConfigChanges } from './ConfigChanges'
import {
  CustomNodeTitle,
  DestinationTitle,
  DestinationType,
} from './DestinationType'
import { Attributes } from './Attributes'
import { cloneDeep } from '../../utility'
import { UploadIngestedButton, DoIngestButton } from './DoIngestButton'
import { SourceFile } from './SourceFile'
import { NodeChildren } from './NodeChildren'
import { useHasChanges } from './hooks'
import { ChangeableContent } from './utility'
import { IngestionPreview } from './IngestionPreview'
import { GDriveLogin } from '../../components/tree/googleApis/GDriveLogin'

export function IngestionConfig() {
  const { partnerId } = useParams()
  useSetAtom(partnerIdAtom)(partnerId ?? '')
  const currentConfig = useAtomValue(configFileAtom)
  const [editedConfig, setEditedConfig] = useAtom(editedConfigAtom)
  const userMessage = useAtomValue(userMessageAtom)

  useEffect(() => {
    if (!editedConfig) {
      setEditedConfig(cloneDeep(currentConfig))
    }
  }, [currentConfig, editedConfig])

  return (
    <Box>
      <Box>{partnerId}</Box>
      <GDriveLogin />
      {userMessage && <Box padding="8px">{userMessage}</Box>}
      <SourceData />
      <CurrentConfig />
    </Box>
  )
}

function SourceData() {
  const partnerId = useAtomValue(partnerIdAtom)
  const fromSource = useAtomValue(configurationFromSourceAtom)
  const setEditingConfig = useSetAtom(editedConfigAtom)

  if (partnerId !== 'Odell') return null

  return (
    <Box padding="5px">
      <SourceFile />
      <Button
        disabled={!fromSource}
        onClick={() => setEditingConfig(fromSource)}
      >
        From Source
      </Button>
      <DoIngestButton />
      <UploadIngestedButton />
    </Box>
  )
}

function CurrentConfig() {
  const currentConfig = useAtomValue(editedConfigAtom)

  if (!currentConfig) return <Box>Nothing Yet</Box>

  return (
    <Box>
      <ConfigChanges />
      {Object.keys(currentConfig).map((key) => (
        <Box key={key} padding="5px">
          <TopLevelKey configKey={key} />
        </Box>
      ))}
    </Box>
  )
}

function TopLevelKey({ configKey }: { configKey: string }) {
  const { configKeyHasChanges } = useHasChanges()
  const changed = configKeyHasChanges(configKey)
  return (
    <Accordion>
      <AccordionSummary>
        <ChangeableContent changed={changed}>{configKey}</ChangeableContent>
      </AccordionSummary>
      <AccordionDetails>
        <TopLevelConfig configKey={configKey} />
      </AccordionDetails>
    </Accordion>
  )
}

function TopLevelConfig({ configKey }: { configKey: string }) {
  return (
    <Box>
      <DestinationType configKey={configKey} />
      <DestinationTitle configKey={configKey} />
      <CustomNodeTitle configKey={configKey} />
      <NodeChildren configKey={configKey} />
      <Attributes configKey={configKey} />
      <IngestionPreview configKey={configKey} />
    </Box>
  )
}
